import Phaser from 'phaser';
import { BiomassScene } from '@/core/BiomassScene';
import { BlobEntity } from '@/entities/BlobEntity';
import { VerminEntity } from '@/entities/VerminEntity';
import { SPECIES } from '@/entities/species';
import { InputController } from '@/input/InputController';
import { HUD } from '@/ui/HUD';
import { SpeechBubble } from '@/ui/SpeechBubble';
import { ConsumePulse } from '@/ui/ConsumePulse';
import { L } from '@/core/Locale';
import { NarrativeDirector } from '@/narrative/NarrativeDirector';
import { UNDERCITY_NARRATIVE } from '@/narrative/undercity.narrative';
import { lettersKnown, LETTER_ORDER } from '@/systems/LiteracyFilter';

const CHAMBER_WIDTH = 1600;
const CHAMBER_HEIGHT = 1000;
const NEST_X = 120;
const NEST_Y = CHAMBER_HEIGHT - 120;

interface Wall {
  x: number;
  y: number;
  w: number;
  h: number;
}

const WALLS: Wall[] = [
  { x: 500, y: 200, w: 200, h: 40 },
  { x: 900, y: 450, w: 40, h: 200 },
  { x: 300, y: 600, w: 260, h: 40 },
  { x: 1100, y: 200, w: 40, h: 220 },
  { x: 1200, y: 700, w: 200, h: 40 },
];

export class UnderCityScene extends BiomassScene {
  private blob!: BlobEntity;
  private vermin: VerminEntity[] = [];
  private walls!: Phaser.Physics.Arcade.StaticGroup;
  private nest!: Phaser.GameObjects.Arc;
  private auraRing!: Phaser.GameObjects.Arc;

  private controls!: InputController;
  private hud!: HUD;
  private humanBubbles = new Map<string, SpeechBubble>();
  private nextAmbientSoundMs = 0;
  private narrative!: NarrativeDirector;
  private consumePulses = new Map<string, ConsumePulse>();
  private announcedLetters = new Set<string>();

  private contacts = new Map<string, VerminEntity>();
  private deathCooldownMs = 0;
  private disposers: Array<() => void> = [];

  private readonly MIN_MASS = 0.15;
  private readonly INVULN_MS = 1400;

  constructor() {
    super({ key: 'UnderCity' });
  }

  protected resetSceneState(): void {
    this.vermin = [];
    this.contacts.clear();
    this.deathCooldownMs = 0;
    this.controls?.dispose();
    for (const d of this.disposers) d();
    this.disposers = [];
    for (const b of this.humanBubbles.values()) b.destroy();
    this.humanBubbles.clear();
    for (const p of this.consumePulses.values()) p.destroy();
    this.consumePulses.clear();
    this.nextAmbientSoundMs = 0;
    this.announcedLetters.clear();
  }

  protected onSceneCreate(): void {
    this.cameras.main.setBackgroundColor('#0b0b0b');
    this.physics.world.setBounds(0, 0, CHAMBER_WIDTH, CHAMBER_HEIGHT);

    this.narrative = new NarrativeDirector(UNDERCITY_NARRATIVE);
    this.buildMap();
    this.spawnPlayer();
    this.spawnVermin();
    this.setupCollisions();
    this.setupInput();
    this.setupHUD();
    this.wireEvents();

    this.cameras.main.startFollow(this.blob, true, 0.1, 0.1);
    this.cameras.main.setBounds(0, 0, CHAMBER_WIDTH, CHAMBER_HEIGHT);
    this.applyTierZoom(this.state.mass.currentTier, false);

    this.state.bus.emit('scene:enter', { sceneKey: 'UnderCity' });
    this.hud.showNarrator(L('narrator.intro'), 3500);
  }

  update(_time: number, deltaMs: number): void {
    this.controls.beginFrame();

    if (this.deathCooldownMs > 0) {
      this.deathCooldownMs -= deltaMs;
      return;
    }

    const move = this.controls.getMoveVector();
    // Base ooze speed — tuned for touch/controller feel. Tier drop
    // applied on top (bigger blob = marginally slower).
    const baseSpeed = 130;
    const speed = baseSpeed * this.state.mass.speedMultiplier();
    this.blob.applyImpulse(move.x, move.y, speed);

    const blobEntity = this.blob.entity;
    const blobPos = new Phaser.Math.Vector2(this.blob.x, this.blob.y);

    for (const v of this.vermin) {
      if (!v.active) continue;
      v.update(
        deltaMs,
        blobEntity,
        blobPos,
        this.state.relations,
        this.state.perception,
        this.state.threatAura,
      );
    }

    this.state.consumption.tick(blobEntity, deltaMs, (preyId) =>
      this.contacts.has(preyId),
    );

    this.contacts.clear();
    this.hud.refresh();

    // Keep the aura ring following the blob and scaled to current radius.
    const r = this.state.threatAura.radius;
    this.auraRing.setPosition(this.blob.x, this.blob.y);
    if (r > 0) {
      this.auraRing.setRadius(r);
      this.auraRing.setVisible(true);
    } else {
      this.auraRing.setVisible(false);
    }

    // Track speech bubbles to their owners each frame
    for (const bubble of this.humanBubbles.values()) bubble.follow();

    // Track consume pulses (tethers + rings follow blob + prey)
    for (const pulse of this.consumePulses.values()) pulse.follow();

    // Ambient human chatter — blob "hears" it
    this.nextAmbientSoundMs -= deltaMs;
    if (this.nextAmbientSoundMs <= 0) {
      this.triggerAmbientSound();
      this.nextAmbientSoundMs = 6000 + Math.random() * 8000;
    }
  }

  private buildMap(): void {
    const floor = this.add.graphics();
    floor.fillGradientStyle(0x1a1510, 0x1a1510, 0x0a0707, 0x0a0707, 1);
    floor.fillRect(0, 0, CHAMBER_WIDTH, CHAMBER_HEIGHT);

    const grime = this.add.graphics();
    grime.fillStyle(0x2a2520, 0.5);
    for (let i = 0; i < 300; i++) {
      grime.fillCircle(
        Math.random() * CHAMBER_WIDTH,
        Math.random() * CHAMBER_HEIGHT,
        1 + Math.random() * 2,
      );
    }

    this.walls = this.physics.add.staticGroup();
    for (const w of WALLS) {
      const r = this.add.rectangle(w.x + w.w / 2, w.y + w.h / 2, w.w, w.h, 0x303030);
      this.physics.add.existing(r, true);
      this.walls.add(r);
    }
    const outer = [
      { x: 0, y: -20, w: CHAMBER_WIDTH, h: 40 },
      { x: 0, y: CHAMBER_HEIGHT - 20, w: CHAMBER_WIDTH, h: 40 },
      { x: -20, y: 0, w: 40, h: CHAMBER_HEIGHT },
      { x: CHAMBER_WIDTH - 20, y: 0, w: 40, h: CHAMBER_HEIGHT },
    ];
    for (const w of outer) {
      const r = this.add.rectangle(w.x + w.w / 2, w.y + w.h / 2, w.w, w.h, 0x202020);
      this.physics.add.existing(r, true);
      this.walls.add(r);
    }

    this.nest = this.add.circle(NEST_X, NEST_Y, 28, 0x3d5a3d, 0.5).setDepth(-1);
    this.add.circle(NEST_X, NEST_Y, 36, 0x000000, 0).setStrokeStyle(2, 0x6cff6c, 0.6);
    void this.nest;
  }

  private spawnPlayer(): void {
    this.blob = new BlobEntity(this, NEST_X, NEST_Y, this.state.mass);
    this.auraRing = this.add
      .circle(NEST_X, NEST_Y, 1, 0x6cff6c, 0)
      .setStrokeStyle(1, 0x6cff6c, 0.12)
      .setDepth(-1);
  }

  private spawnVermin(): void {
    const spawnArea = (padding = 120) => ({
      x: padding + Math.random() * (CHAMBER_WIDTH - padding * 2),
      y: padding + Math.random() * (CHAMBER_HEIGHT - padding * 2),
    });

    const add = (speciesId: string, count: number) => {
      for (let i = 0; i < count; i++) {
        let tries = 0;
        while (tries++ < 20) {
          const pos = spawnArea();
          const blobDist = Phaser.Math.Distance.Between(pos.x, pos.y, NEST_X, NEST_Y);
          if (blobDist < 200) continue;
          const v = new VerminEntity(this, pos.x, pos.y, SPECIES[speciesId], `${speciesId}-${i}`);
          this.vermin.push(v);
          break;
        }
      }
    };

    // Early food is non-living: corpses, mold, sludge, decay. The
    // blob is too slow to catch most living things until it grows a
    // threat aura and unlocks stealth/ambush.
    add('sludge', 42);
    add('mold', 24);
    add('decay_pile', 18);
    add('corpse_small', 14);
    add('corpse_medium', 8);
    add('carcass', 3);
    // A few soggy books someone left behind. Organic. Edible.
    // Reading is an acquired taste. Future arcs seed far more.
    add('book', 6);

    // Living vermin — faster than the ooze, flee at the edge of aura.
    // Harder to catch; rewards a committed strategy.
    add('cockroach', 12);
    add('larva', 8);
    add('rat', 8);
    add('cat', 3);
    add('alligator', 1);
    add('human', 2);
  }

  private setupCollisions(): void {
    this.physics.add.collider(this.blob, this.walls);
    for (const v of this.vermin) {
      this.physics.add.collider(v, this.walls);
    }
    const group = this.physics.add.group(this.vermin);
    this.physics.add.collider(group, group);
    this.physics.add.overlap(this.blob, group, (_blob, other) => {
      const v = other as VerminEntity;
      if (!v.active) return;
      this.handleContact(v);
    });

    // Attach a SpeechBubble to each human (they're the only vocal species in Arc 0)
    for (const v of this.vermin) {
      if (v.speciesId === 'human') {
        this.humanBubbles.set(v.entityId, new SpeechBubble(this, v));
      }
    }
  }

  private readonly AMBIENT_LINES = [
    'hey',
    'you see that',
    'quiet',
    'shut up',
    'pass me that',
    'where is he',
    'cough',
    'tired',
  ];

  private readonly LETTER_LINES: Record<string, string> = {
    e: '[the most common shape. soft. open. an opening.]',
    t: '[two lines. one crosses the other.]',
    a: '[a triangle with a ground beneath it.]',
    o: '[a closed circle. like a mouth around a word.]',
    i: '[a line with a dot. a self. alone.]',
    n: '[a bridge. it goes somewhere.]',
    s: '[a curve that turns back. like water.]',
    h: '[two pillars with a floor between. I understand.]',
    r: '[a stem. a head. a foot that kicks.]',
    d: '[round on the right. firm on the left.]',
  };

  private checkLetterUnlocks(): void {
    const lit = this.state.dna.get('literacy');
    const known = lettersKnown(lit);
    for (const letter of LETTER_ORDER) {
      if (!known.has(letter)) break;
      if (this.announcedLetters.has(letter)) continue;
      this.announcedLetters.add(letter);
      const line = this.LETTER_LINES[letter] ?? `[a new shape. ${letter}.]`;
      this.dispatchNarration([line]);
    }
  }

  /** Fire a sequence of narrator lines with stagger so they don't clobber each other. */
  private dispatchNarration(lines: readonly string[]): void {
    const stagger = 3200;
    for (let i = 0; i < lines.length; i++) {
      if (i === 0) {
        this.hud.showNarrator(lines[i], 4200);
      } else {
        this.time.delayedCall(i * stagger, () => this.hud.showNarrator(lines[i], 4200));
      }
    }
  }

  private triggerAmbientSound(): void {
    const humans = this.vermin.filter((v) => v.active && v.speciesId === 'human');
    if (humans.length === 0) return;
    const speaker = humans[Math.floor(Math.random() * humans.length)];
    const bubble = this.humanBubbles.get(speaker.entityId);
    if (!bubble) return;
    const line = this.AMBIENT_LINES[Math.floor(Math.random() * this.AMBIENT_LINES.length)];
    bubble.say(line, this.state.gate.currentLevel, 2800);
  }

  private setupInput(): void {
    this.controls = new InputController(this, this.services.settings);
  }

  private setupHUD(): void {
    this.hud = new HUD(
      this,
      this.state.mass,
      this.state.brain,
      this.state.gate,
      this.state.abilities,
      this.state.dna,
    );
  }

  private wireEvents(): void {
    const bus = this.state.bus;

    this.disposers.push(
      bus.on('consume:complete', (e) => {
        const prey = this.findVerminById(e.preyId);
        const speciesDef = prey ? prey.species : undefined;
        // Pulse visual for this consume is done
        const pulse = this.consumePulses.get(e.preyId);
        if (pulse) {
          pulse.destroy();
          this.consumePulses.delete(e.preyId);
        }
        prey?.destroy();
        this.vermin = this.vermin.filter((v) => v.entityId !== e.preyId);
        this.hud.pulseTier();
        this.flashText(`+${e.biomassGained.toFixed(1)}`, this.blob.x, this.blob.y - 30);

        // DNA: accumulate the species' trait profile into the blob's genome.
        if (speciesDef?.dna) this.state.dna.contribute(speciesDef.dna);

        // Narrative triggers: firsts + categories + counts
        const categories = speciesDef?.categories ?? [];
        const lines = this.narrative.processConsume(e.speciesId, categories);
        this.dispatchNarration(lines);

        // Letter-learning narration
        this.checkLetterUnlocks();
      }),
    );

    this.disposers.push(
      bus.on('mass:tier-change', ({ tier }) => {
        const key = `narrator.tier.${tier}`;
        this.hud.showNarrator(L(key), 2500);
        this.applyTierZoom(tier, true);
      }),
    );

    this.disposers.push(
      bus.on('consume:start', (e) => {
        const prey = this.findVerminById(e.preyId);
        if (!prey) return;
        const pulse = new ConsumePulse(this, this.blob, prey, prey.species.bodyRadius);
        this.consumePulses.set(e.preyId, pulse);
      }),
    );

    this.disposers.push(
      bus.on('consume:interrupted', (e) => {
        const p = this.consumePulses.get(e.preyId);
        if (p) {
          p.destroy();
          this.consumePulses.delete(e.preyId);
        }
      }),
    );

    this.disposers.push(
      bus.on('brain:level-change', ({ level }) => {
        if (level >= 1) this.hud.showNarrator(L(`narrator.brain.${level}`), 2200);
      }),
    );

    this.disposers.push(
      bus.on('sentience:change', ({ level }) => {
        this.hud.refresh();
        if (level === 4) this.cameras.main.flash(1500, 200, 255, 200);
      }),
    );
  }

  private handleContact(v: VerminEntity): void {
    if (this.deathCooldownMs > 0) return;
    this.contacts.set(v.entityId, v);
    const result = this.state.consumption.attemptContact(this.blob.entity, v.entity);
    if (result === 'lethal') {
      this.onBlobDeath(v);
    }
  }

  private onBlobDeath(by: VerminEntity): void {
    this.deathCooldownMs = this.INVULN_MS;
    this.state.consumption.cancelAllFor(this.blob.entityId);

    const current = this.state.mass.currentMass;
    const proposed = current * 0.6;
    const next = Math.max(this.MIN_MASS, proposed);
    this.state.mass.subtract(current - next);

    this.hud.pulseThreat(1);
    this.hud.showNarrator(`[${by.species.displayName}. death. return.]`, 2500);
    this.cameras.main.flash(300, 255, 0, 0);
    this.blob.teleport(NEST_X, NEST_Y);

    this.state.bus.emit('blob:death', { causeSpeciesId: by.speciesId });

    const body = this.blob.body as Phaser.Physics.Arcade.Body | null;
    if (body) body.enable = false;
    this.blob.setAlpha(0.4);
    this.time.delayedCall(this.INVULN_MS, () => {
      const b = this.blob.body as Phaser.Physics.Arcade.Body | null;
      if (b) b.enable = true;
      this.blob.setAlpha(1);
      this.state.bus.emit('blob:respawn', {});
    });
  }

  private findVerminById(id: string): VerminEntity | undefined {
    return this.vermin.find((v) => v.entityId === id);
  }

  /**
   * Zoom tight at tier 1 — the blob is tiny, the world is huge. As
   * growth earns perceptual range, the camera pulls back. A sensory-
   * upgrade tech node in a later arc will expand this curve further.
   */
  private applyTierZoom(tier: number, animate: boolean): void {
    const zoom = Math.max(0.95, 2.1 - (tier - 1) * 0.22);
    if (animate) {
      this.tweens.add({
        targets: this.cameras.main,
        zoom,
        duration: 900,
        ease: 'Cubic.easeOut',
      });
    } else {
      this.cameras.main.setZoom(zoom);
    }
  }

  private flashText(text: string, x: number, y: number): void {
    const t = this.add
      .text(x, y, text, { fontSize: '14px', color: '#9cff9c', fontFamily: 'monospace' })
      .setOrigin(0.5)
      .setDepth(900);
    this.tweens.add({
      targets: t,
      y: y - 30,
      alpha: 0,
      duration: 900,
      onComplete: () => t.destroy(),
    });
  }
}
